import { InsightPagesAPI } from "@radarly/api/insight-pages";
import { DiscoveryRow, FeedbackReason } from "@radarly/models/discovery";
import {
  InsightPage,
  InsightPageRecurringSnapshot,
  InsightPageSnapshotFactory,
  RecurringSnapshotPayload,
} from "@radarly/models/insight-pages/model";
import { InsightPageRepo, Pagination } from "./InsightPageRepo";

interface Dependencies {
  insightPagesAPI: InsightPagesAPI;
}

export class RESTInsightPageRepo implements InsightPageRepo {
  private insightPagesAPI: InsightPagesAPI;
  public constructor(dependencies: Dependencies) {
    this.insightPagesAPI = dependencies.insightPagesAPI;
  }
  public async getInsightPages(projectId: number, workspaceId: number) {
    return await this.insightPagesAPI.getInsightPages(projectId, workspaceId);
  }
  public async getLeanInsightPages(
    projectId: number,
    workspaceId: number,
    pagination: Pagination
  ) {
    return await this.insightPagesAPI.getLeanInsightPages(
      projectId,
      workspaceId,
      pagination
    );
  }
  public async getInsightPage(projectId: number, id: InsightPage["id"]) {
    return await this.insightPagesAPI.getInsightPage(projectId, id);
  }
  public async setInsightPage(
    projectId: number,
    page: InsightPage,
    template?: string
  ) {
    return await this.insightPagesAPI.setInsightPage(projectId, page, template);
  }
  public async deleteInsightPage(projectId: number, id: InsightPage["id"]) {
    return await this.insightPagesAPI.deleteInsightPage(projectId, id);
  }
  public async sendSnapshot(
    projectId: number,
    id: InsightPage["id"],
    snapshot: InsightPageSnapshotFactory
  ) {
    return await this.insightPagesAPI.sendSnapshot(projectId, id, snapshot);
  }

  public async sendRecurringSnapshot(
    projectId: number,
    id: InsightPage["id"],
    snapshot: RecurringSnapshotPayload
  ) {
    return await this.insightPagesAPI.sendRecurringSnapshot(
      projectId,
      id,
      snapshot
    );
  }

  public async shareInsightPageAccess(
    projectId: number,
    id: InsightPage["id"],
    access: InsightPage["access"],
    sharedWith: InsightPage["sharedWith"]
  ) {
    return await this.insightPagesAPI.shareInsightPageAccess(
      projectId,
      id,
      access,
      sharedWith
    );
  }

  public async getRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    all?: boolean
  ) {
    return await this.insightPagesAPI.getRecurringSnapshots(projectId, id, all);
  }

  public async batchUpdateRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    snapshots: Array<{
      id: InsightPageRecurringSnapshot["id"];
      page: InsightPage;
    }>
  ) {
    return await this.insightPagesAPI.batchUpdateRecurringSnapshots(
      projectId,
      id,
      snapshots
    );
  }

  public async deleteRecurringSnapshot(
    projectId: number,
    pageId: InsightPage["id"],
    recurringSnapshotId: InsightPageRecurringSnapshot["id"]
  ) {
    return await this.insightPagesAPI.deleteRecurringSnapshot(
      projectId,
      pageId,
      recurringSnapshotId
    );
  }

  public async getInvalidRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    timezone: InsightPage["timezone"]
  ) {
    return await this.insightPagesAPI.getInvalidRecurringSnapshots(
      projectId,
      id,
      timezone
    );
  }

  public async getDiscoveryPage(
    projectId: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ) {
    return await this.insightPagesAPI.getDiscovery(
      projectId,
      workspaceId,
      params
    );
  }

  public async getDiscoveryStatus(
    projectId: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ) {
    return await this.insightPagesAPI.getDiscoveryStatus(
      projectId,
      workspaceId,
      params
    );
  }

  public async getDiscoveryDetails(
    projectId: number,
    workspaceId: number,
    meta: DiscoveryRow["meta"],
    template?: string
  ) {
    return await this.insightPagesAPI.getDiscoveryDetails(
      projectId,
      workspaceId,
      meta,
      template
    );
  }

  public async sendDiscoveryFeedback(
    projectId: number,
    workspaceId: number,
    page: InsightPage,
    feedback: boolean,
    reason: FeedbackReason,
    message?: string
  ) {
    return await this.insightPagesAPI.sendDiscoveryFeedback(
      projectId,
      workspaceId,
      page,
      feedback,
      reason,
      message
    );
  }

  public async searchPages(
    projectId: number,
    search: string,
    workspaceId: number,
    pagination: Pagination,
    sort: string
  ) {
    return await this.insightPagesAPI.searchPages(
      projectId,
      search,
      workspaceId,
      pagination,
      sort
    );
  }

  public async saveDiscoverySnapshot(
    projectId: number,
    snapshot: InsightPage
  ): Promise<{ s3Id: string }> {
    return await this.insightPagesAPI.saveDiscoverySnapshot(
      projectId,
      snapshot
    );
  }
}
